import { Box, Button, Fade } from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import InputMask from 'react-input-mask';
import { useFirebase } from "../../../hooks";
import { PhoneVerification } from "../../../components";
import { useLoginWithFirebaseMutation, useCheckAccountMutation } from "../../../store/api/usersApiSlice";
import { useDispatch } from "react-redux";
import { setCredentials } from "../../../store/slices/authSlice";
import { toast } from "react-toastify";
import { Phone } from "@mui/icons-material";
import RecaptchaV2Host from "../../../components/RecaptchaV2Host";
import { reportClientAuthError } from "../../../utils/clientAuthErrorReport";
import { appColors } from "../../../constants/theme";
import {
  PhoneNumberFormContainer,
  PhoneNumberFormFieldContainer,
  PhoneNumberInputContainer,
  StyledPhoneTextField,
  CountryCodeTextField,
  VerificationOverlay,
  RecaptchaContainer
} from "../style";

const logLoginFailure = (stage, details = {}) => {
  console.error("[Login/Phone] Giriş başarısız:", { stage, ...details });
};

const PhoneNumberForm = ({
  setLoading,
  superLogin = false,
  onPhoneNumberChange,
  showCaptcha = false,
  siteKey,
  isCaptchaVerified = false,
  captchaToken: externalCaptchaToken,
  onCaptcha,
}) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const {
    generateRecaptcha,
    signInWithPhoneNumberFirebase,
    getSignature,
    getFCMToken,
  } = useFirebase();

  const [loginWithFirebase] = useLoginWithFirebaseMutation();
  const [checkAccount] = useCheckAccountMutation();

  const recaptchaContainerRef = useRef(null);
  const recaptchaContainerId = "login-phone-recaptcha";

  const [showVerification, setShowVerification] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verificationKey, setVerificationKey] = useState(0);

  const [phoneNumberDisable, setPhoneNumberDisable] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isCaptchaVerifiedLocal, setIsCaptchaVerifiedLocal] = useState(false);

  const [lastCodeSentTime, setLastCodeSentTime] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  /** Checkbox reCAPTCHA: reset() grecaptcha ile DOM yarışında null.style hatasına yol açabiliyor */
  const [visibleRecaptchaKey, setVisibleRecaptchaKey] = useState(0);

  const COOLDOWN_DURATION = 60;
  const COOLDOWN_DURATION_AFTER_ERROR = 900;

  const getStorageKey = (phoneNumber) => {
    const cleanPhone = phoneNumber?.replace(/\s/g, '') || '';
    return `sms_cooldown_${cleanPhone}`;
  };

  const isCaptchaVerifiedFinal = onCaptcha ? isCaptchaVerified : isCaptchaVerifiedLocal;
  const captchaTokenFinal = onCaptcha ? externalCaptchaToken : captchaToken;
  const validationSchema = Yup.object().shape({
    phoneNumber: Yup.string()
      .required(t("error_generic_invalid_input"))
      .min(13, t("error_generic_invalid_input"))
      .max(13, t("error_generic_invalid_input"))
      .matches(/^[0-9\s]+$/, t("error_generic_invalid_input"))
      .test('phone-format', t("error_generic_invalid_input"), (value) => {
        if (!value) return false;
        const numbersOnly = value.replace(/\s/g, '');
        return numbersOnly.length === 10 && /^[0-9]+$/.test(numbersOnly);
      }),
    countryCode: Yup.string()
      .matches(/^\d+$/, t("error_generic_invalid_input"))
      .required(t("error_generic_invalid_input"))
      .max(4, t("error_generic_invalid_input")),
  });

  const {
    handleSubmit,
    handleChange,
    handleBlur,
    values,
    touched,
    errors,
  } = useFormik({
    initialValues: {
      phoneNumber: "",
      countryCode: "90",
    },
    validationSchema,
    validateOnBlur: true,
    onSubmit: () => handleLogin(values),
  });

  useEffect(() => {
    const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '');
    if (cleanPhoneNumber && cleanPhoneNumber.length === 10) {
      const storageKey = getStorageKey(cleanPhoneNumber);
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const { timestamp, duration } = JSON.parse(stored);
          const now = Date.now();
          const elapsed = Math.floor((now - timestamp) / 1000);
          const remaining = duration - elapsed;

          if (remaining > 0) {
            setLastCodeSentTime(timestamp);
            setCooldownSeconds(remaining);
          } else {
            localStorage.removeItem(storageKey);
          }
        } catch (e) {
          console.error('Cooldown bilgisi okunamadı:', e);
        }
      }
    }
  }, [values.phoneNumber]);

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value || '';
    const cleanedValue = value.replace(/_/g, '');
    handleChange({
      target: {
        name: 'phoneNumber',
        value: cleanedValue
      }
    });
  };

  useEffect(() => {
    const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '');
    if (cooldownSeconds > 0 && lastCodeSentTime && cleanPhoneNumber && cleanPhoneNumber.length === 10) {
      const storageKey = getStorageKey(cleanPhoneNumber);
      localStorage.setItem(storageKey, JSON.stringify({
        timestamp: lastCodeSentTime,
        duration: cooldownSeconds + Math.floor((Date.now() - lastCodeSentTime) / 1000)
      }));

      const interval = setInterval(() => {
        const now = Date.now();
        const elapsed = Math.floor((now - lastCodeSentTime) / 1000);
        const remaining = cooldownSeconds - elapsed;

        if (remaining <= 0) {
          setCooldownSeconds(0);
          setLastCodeSentTime(null);
          localStorage.removeItem(storageKey);
          clearInterval(interval);
        } else {
          setCooldownSeconds(remaining);
          localStorage.setItem(storageKey, JSON.stringify({
            timestamp: lastCodeSentTime,
            duration: remaining + elapsed
          }));
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [cooldownSeconds, lastCodeSentTime, values.phoneNumber]);

  useEffect(() => {
    if (onPhoneNumberChange) {
      onPhoneNumberChange(values.phoneNumber);
    }
  }, [values.phoneNumber, onPhoneNumberChange]);

  useEffect(() => {
    let el = document.getElementById(recaptchaContainerId);
    if (!el) {
      el = document.createElement("div");
      el.id = recaptchaContainerId;
      el.style.display = "none";
      el.style.position = "absolute";
      el.style.left = "-9999px";
      el.style.top = "-9999px";
      el.style.visibility = "hidden";
      document.body.appendChild(el);
    }
    recaptchaContainerRef.current = el;
  }, []);

  const handleLogin = async (values) => {
    if (!captchaTokenFinal) {
      setLoading(false);
      toast.error(t("please_complete_captcha"));
      return;
    }

    const now = Date.now();
    if (lastCodeSentTime && cooldownSeconds > 0) {
      const elapsed = Math.floor((now - lastCodeSentTime) / 1000);
      const remaining = cooldownSeconds - elapsed;
      if (remaining > 0) {
        toast.error(t("please_wait_before_resending_code", { seconds: remaining }) || `Lütfen ${remaining} saniye bekleyin`);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      setPhoneNumberDisable(true);

      const cleanPhoneNumber = values.phoneNumber.replace(/\s/g, '');
      const fullPhoneNumber = `+${values.countryCode}${cleanPhoneNumber}`;

      const checkRes = await checkAccount({
        email: "",
        phone: fullPhoneNumber
      }).unwrap();

      const isPhoneUsable = checkRes?.isPhoneUsable !== undefined
        ? checkRes.isPhoneUsable
        : checkRes?.IsPhoneUsable;

      if (isPhoneUsable === true) {
        logLoginFailure("phone_not_registered", {
          phone: fullPhoneNumber,
          checkRes,
        });
        setLoading(false);
        setPhoneNumberDisable(false);
        // TODO: hata mesajı geri açılacaktır.
        // toast.error(t("phone_not_found"));
        toast.error(t("wrong_email_address_or_password"));
        if (onCaptcha) {
          onCaptcha(null);
        } else {
          setIsCaptchaVerifiedLocal(false);
          setCaptchaToken(null);
        }
        setVisibleRecaptchaKey((k) => k + 1);
        return;
      }

      setPhoneNumber(fullPhoneNumber);

      if (!recaptchaContainerRef.current) {
        setLoading(false);
        setPhoneNumberDisable(false);
        toast.error(t("recaptcha_container_not_found"));
        return;
      }

      // reCAPTCHA verifier'ı oluştur veya mevcut olanı kullan
      let appVerifier = window.recaptchaVerifier;
      if (!appVerifier || window.recaptchaContainer !== recaptchaContainerRef.current) {
        appVerifier = generateRecaptcha(recaptchaContainerRef.current);
        if (!appVerifier) {
          setLoading(false);
          setPhoneNumberDisable(false);
          toast.error(t("recaptcha_creation_failed"));
          return;
        }
      }

      const confirmationResult = await signInWithPhoneNumberFirebase(
        fullPhoneNumber,
        appVerifier,
        () => {
          setShowVerification(true);
          const now = Date.now();
          setLastCodeSentTime(now);
          setCooldownSeconds(COOLDOWN_DURATION);
          const storageKey = getStorageKey(cleanPhoneNumber);
          localStorage.setItem(storageKey, JSON.stringify({
            timestamp: now,
            duration: COOLDOWN_DURATION
          }));
        }
      );

      // Güvenlik fallback'i: callback herhangi bir sebeple tetiklenmezse
      // doğrulama ekranını yine aç.
      if (confirmationResult) {
        setShowVerification(true);
      }
    } catch (err) {
      const isFirebaseError = err?.code?.startsWith('auth/') ||
        err?.message?.includes('Firebase') ||
        err?.message?.includes('too-many-requests') ||
        err?.message?.includes('quota-exceeded') ||
        err?.message?.includes('network-request-failed') ||
        err?.message?.includes('billing-not-enabled');

      console.error('[Login/PhoneNumberForm] Firebase hatası:', {
        code: err?.code,
        message: err?.message,
        error: err
      });

      if (isFirebaseError) {
        if (err?.code === 'auth/too-many-requests') {
          const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '') || '';
          const now = Date.now();
          setLastCodeSentTime(now);
          setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
          const storageKey = getStorageKey(cleanPhoneNumber);
          localStorage.setItem(storageKey, JSON.stringify({
            timestamp: now,
            duration: COOLDOWN_DURATION_AFTER_ERROR
          }));
          toast.error(t("too_many_attempts_sms_send", { minutes: Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60) }) || `SMS gönderme limiti aşıldı. Lütfen ${Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60)} dakika bekleyin.`);
        } else if (err?.code === 'auth/quota-exceeded') {
          setLastCodeSentTime(Date.now());
          setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
          toast.error(t("sms_quota_exceeded"));
        } else if (err?.code === 'auth/network-request-failed') {
          toast.error(t("network_error"));
        } else if (err?.code === 'auth/billing-not-enabled') {
          // TODO: hata mesajı geri açılacaktır.
          // toast.error(t("phone_not_found"));
          toast.error(t("wrong_email_address_or_password"));
        } else {
          toast.error(t("wrong_email_address_or_password"));
        }
      } else {
        toast.error(t("wrong_email_address_or_password"));
      }
      setPhoneNumberDisable(false);
      if (onCaptcha) {
        onCaptcha(null);
      } else {
        setIsCaptchaVerifiedLocal(false);
        setCaptchaToken(null);
      }
      setVisibleRecaptchaKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    const cleanPhoneNumber = values.phoneNumber.replace(/\s/g, '');
    const storageKey = getStorageKey(cleanPhoneNumber);
    const stored = localStorage.getItem(storageKey);

    let remaining = 0;
    if (stored) {
      try {
        const { timestamp, duration } = JSON.parse(stored);
        const now = Date.now();
        const elapsed = Math.floor((now - timestamp) / 1000);
        remaining = duration - elapsed;

        if (remaining > 0) {
          setLastCodeSentTime(timestamp);
          setCooldownSeconds(remaining);
        } else {
          localStorage.removeItem(storageKey);
        }
      } catch (e) {
        console.error('Cooldown bilgisi okunamadı:', e);
      }
    }

    if (remaining <= 0 && lastCodeSentTime && cooldownSeconds > 0) {
      const now = Date.now();
      const elapsed = Math.floor((now - lastCodeSentTime) / 1000);
      remaining = cooldownSeconds - elapsed;
    }

    if (remaining > 0) {
      const minutes = Math.floor(remaining / 60);
      const seconds = remaining % 60;
      const timeText = minutes > 0 ? `${minutes} dakika ${seconds} saniye` : `${seconds} saniye`;
      toast.error(t("please_wait_before_resending_code", { seconds: remaining }) || `Lütfen ${timeText} bekleyin`);
      return;
    }

    try {
      setPhoneNumberDisable(true);
      const fullPhoneNumber = `+${values.countryCode}${cleanPhoneNumber}`;

      const checkRes = await checkAccount({
        email: "",
        phone: fullPhoneNumber
      }).unwrap();

      const isPhoneUsable = checkRes?.isPhoneUsable !== undefined
        ? checkRes.isPhoneUsable
        : checkRes?.IsPhoneUsable;

      if (isPhoneUsable === true) {
        setPhoneNumberDisable(false);
        // TODO: hata mesajı geri açılacaktır.
        // toast.error(t("phone_not_found"));
        toast.error(t("wrong_email_address_or_password"));
        return;
      }

      setPhoneNumber(fullPhoneNumber);

      if (!recaptchaContainerRef.current) {
        setPhoneNumberDisable(false);
        toast.error(t("recaptcha_container_not_found"));
        return;
      }

      // reCAPTCHA verifier'ı oluştur veya mevcut olanı kullan
      let appVerifier = window.recaptchaVerifier;
      if (!appVerifier || window.recaptchaContainer !== recaptchaContainerRef.current) {
        appVerifier = generateRecaptcha(recaptchaContainerRef.current);
        if (!appVerifier) {
          setPhoneNumberDisable(false);
          toast.error(t("recaptcha_creation_failed"));
          return;
        }
      }

      const confirmationResult = await signInWithPhoneNumberFirebase(
        fullPhoneNumber,
        appVerifier,
        () => {
          setShowVerification(true);
          setVerificationKey(prev => prev + 1);
          const now = Date.now();
          setLastCodeSentTime(now);
          setCooldownSeconds(COOLDOWN_DURATION);
          const storageKey = getStorageKey(cleanPhoneNumber);
          localStorage.setItem(storageKey, JSON.stringify({
            timestamp: now,
            duration: COOLDOWN_DURATION
          }));
        }
      );

      if (confirmationResult) {
        setShowVerification(true);
      }
    } catch (err) {
      console.error('[Login/PhoneNumberForm] Kod yeniden gönderme hatası:', err);
      console.error('[Login/PhoneNumberForm] Hata kodu:', err?.code);
      console.error('[Login/PhoneNumberForm] Hata mesajı:', err?.message);

      // Timeout hatası kontrolü - önce timeout kontrolü yap
      const isTimeoutError = err?.message?.toLowerCase().includes('timeout') ||
        err?.name === 'TimeoutError' ||
        err?.message === 'Timeout' ||
        err?.toString().toLowerCase().includes('timeout') ||
        err?.code === 'auth/timeout';
      
      if (isTimeoutError) {
        toast.error(t("recaptcha_verification_timeout"));
        setPhoneNumberDisable(false);
        return;
      }

      const isFirebaseError = err?.code?.startsWith('auth/') ||
        err?.message?.includes('Firebase') ||
        err?.message?.includes('too-many-requests') ||
        err?.message?.includes('quota-exceeded') ||
        err?.message?.includes('network-request-failed') ||
        err?.message?.includes('billing-not-enabled');

      console.error('[Login/PhoneNumberForm] Firebase hatası:', {
        code: err?.code,
        message: err?.message,
        error: err
      });

      if (isFirebaseError) {
          if (err?.code === 'auth/too-many-requests') {
            setLastCodeSentTime(Date.now());
            setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
            toast.error(t("too_many_attempts_sms_send", { minutes: Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60) }) || `SMS gönderme limiti aşıldı. Lütfen ${Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60)} dakika bekleyin.`);
          } else if (err?.code === 'auth/quota-exceeded') {
            setLastCodeSentTime(Date.now());
            setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
            toast.error(t("sms_quota_exceeded"));
          } else if (err?.code === 'auth/network-request-failed') {
            toast.error(t("network_error"));
          } else if (err?.code === 'auth/billing-not-enabled') {
            // TODO: hata mesajı geri açılacaktır.
            // toast.error(t("phone_not_found"));
            toast.error(t("wrong_email_address_or_password"));
          } else {
            toast.error(t("wrong_email_address_or_password"));
          }
      } else {
        toast.error(t("wrong_email_address_or_password"));
      }
      setPhoneNumberDisable(false);
      setShowVerification(false);
      setVerificationKey(prev => prev + 1);
      if (onCaptcha) {
        onCaptcha(null);
      } else {
        setIsCaptchaVerifiedLocal(false);
        setCaptchaToken(null);
      }
      setVisibleRecaptchaKey((k) => k + 1);
    }
  };

  const handleEnterCode = async (code) => {
    const rawStr = String(code ?? "");
    const normalizedCode = rawStr.replace(/\D/g, "").slice(0, 6);
    // Firebase 6 haneli sadece rakam bekler; boşluk/özel karakter kabul etmez
    if (normalizedCode.length !== 6) {
      toast.error(t("invalid_verification_code"));
      void reportClientAuthError({
        provider: "FirebasePhone",
        stage: "verifyCode",
        identifier: phoneNumber || null,
        errorCode: "invalid-code-format",
        errorMessage: "Expected 6-digit numeric code",
        hataAciklamaTr: t("invalid_verification_code"),
      });
      return;
    }

    setLoading(true);
    setVerificationLoading(true);
    let confirmationResult = window.confirmationResult;

    if (!confirmationResult) {
      setLoading(false);
      setVerificationLoading(false);
      void reportClientAuthError({
        provider: "FirebasePhone",
        stage: "verifyCode",
        identifier: phoneNumber || null,
        errorCode: "confirmation-session-missing",
        errorMessage: "window.confirmationResult missing",
        hataAciklamaTr: "SMS oturumu bulunamadı; doğrulama penceresi sıfırlanmalı.",
      });
      toast.error(t("verification_code_not_found"));
      setShowVerification(false);
      setPhoneNumberDisable(false);
      setVerificationKey(prev => prev + 1); // Component'i resetle
      return;
    }

    confirmationResult
      .confirm(normalizedCode)
      .then(async (res) => {
        let user = res.user;
        if (!user) {
          throw new Error("no_user_found");
        }

        const idToken = await user.getIdToken();
        const signature = await getSignature(idToken);
        const deviceToken = await getFCMToken();

        const loginPayload = {
          firebaseToken: idToken,
          signature: signature,
          deviceToken: deviceToken,
        };

        const loginResult = await loginWithFirebase(loginPayload).unwrap();

        if (loginResult.isSuccess) {
          setLoading(false);
          setVerificationLoading(false);

          dispatch(
            setCredentials({
              token: loginResult.data.token,
              userType: loginResult.data.userType,
              timeZone: loginResult.data.timeZone,
              isEmailVerified: loginResult.data.isEmailVerified,
              passwordStatus: loginResult.data.passwordStatus,
              status: loginResult.data.status,
            })
          );

          window.location.href = "/";
        } else {
          setLoading(false);
          setVerificationLoading(false);

          const phoneBackendMsgs = loginResult?.error?.messages || {};
          const phoneBackendText = Object.values(phoneBackendMsgs).filter(Boolean).join("; ");
          logLoginFailure("backend_rejected", {
            phone: phoneNumber,
            error: loginResult?.error,
            messages: phoneBackendMsgs,
            message: phoneBackendText,
            loginResult,
          });
          await reportClientAuthError({
            provider: "Backend",
            stage: superLogin ? "login" : "loginWithFirebase",
            failedEndpoint: "/api/DoctorAuth/LoginWithFirebase",
            identifier: phoneNumber || null,
            errorCode: Object.keys(phoneBackendMsgs)[0] || "BACKEND_PHONE_LOGIN_FAILED",
            errorMessage: phoneBackendText || "Phone backend login failed",
            hataAciklamaTr: phoneBackendText
              ? `Sunucu yanıtı: ${phoneBackendText}`
              : "Telefon ile giriş sunucu tarafından reddedildi.",
          });

          if (loginResult?.error?.message?.includes('GNR-00008') ||
            loginResult?.message?.includes('GNR-00008')) {
            toast.error(t("GNR-00008"));
            setTimeout(() => {
              window.location.href = "/login";
            }, 2000);
          } else {
            toast.error(t("wrong_email_address_or_password"));
            setShowVerification(false);
            setPhoneNumberDisable(false);
            setVerificationKey(prev => prev + 1);
          }
        }
      })
      .catch(async (err) => {
        console.error('[Login/PhoneNumberForm] Kod doğrulama hatası:', err);
        console.error('[Login/PhoneNumberForm] Hata kodu:', err?.code);
        console.error('[Login/PhoneNumberForm] Hata mesajı:', err?.message);
        await reportClientAuthError({
          provider: "FirebasePhone",
          stage: "verifyCode",
          identifier: phoneNumber,
          error: err,
          hataAciklamaTr:
            err?.code === "auth/invalid-verification-code"
              ? t("invalid_verification_code")
              : undefined,
        });
        setLoading(false);
        setVerificationLoading(false);

        if (err?.message?.includes('GNR-00008') ||
          err?.data?.message?.includes('GNR-00008') ||
          err?.error?.message?.includes('GNR-00008')) {
          toast.error(t("GNR-00008"));
          setTimeout(() => {
            window.location.href = "/login";
          }, 2000);
        }
        else if (err?.code?.startsWith('auth/') ||
          err?.message?.includes('Firebase') ||
          err?.message?.includes('too-many-requests') ||
          err?.message?.includes('quota-exceeded') ||
          err?.message?.includes('network-request-failed') ||
          err?.message?.includes('billing-not-enabled')) {
          console.error('[Login/PhoneNumberForm] Firebase hatası:', {
            code: err?.code,
            message: err?.message,
            error: err
          });

          if (err?.code === 'auth/invalid-verification-code') {
            toast.error(t("wrong_email_address_or_password"));
            setShowVerification(false);
            setPhoneNumberDisable(false);
            setVerificationKey(prev => prev + 1);
          } else if (err?.code === 'auth/code-expired' || err?.code === 'auth/expired-action-code') {
            toast.error(t("wrong_email_address_or_password"));
            setShowVerification(false);
            setPhoneNumberDisable(false);
            setVerificationKey(prev => prev + 1);
          } else if (err?.code === 'auth/too-many-requests') {
            const cleanPhoneNumber = phoneNumber?.replace(/\+90/g, '').replace(/\s/g, '') || values.phoneNumber?.replace(/\s/g, '') || '';
            const now = Date.now();
            setLastCodeSentTime(now);
            setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
            const storageKey = getStorageKey(cleanPhoneNumber);
            localStorage.setItem(storageKey, JSON.stringify({
              timestamp: now,
              duration: COOLDOWN_DURATION_AFTER_ERROR
            }));
            toast.error(t("too_many_attempts_wait", { minutes: Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60) }) || `Çok fazla deneme yapıldı. Lütfen ${Math.floor(COOLDOWN_DURATION_AFTER_ERROR / 60)} dakika bekleyin.`);
          } else if (err?.code === 'auth/quota-exceeded') {
            const cleanPhoneNumber = phoneNumber?.replace(/\+90/g, '').replace(/\s/g, '') || values.phoneNumber?.replace(/\s/g, '') || '';
            const now = Date.now();
            setLastCodeSentTime(now);
            setCooldownSeconds(COOLDOWN_DURATION_AFTER_ERROR);
            const storageKey = getStorageKey(cleanPhoneNumber);
            localStorage.setItem(storageKey, JSON.stringify({
              timestamp: now,
              duration: COOLDOWN_DURATION_AFTER_ERROR
            }));
            toast.error(t("sms_quota_exceeded"));
          } else if (err?.code === 'auth/network-request-failed') {
            toast.error(t("wrong_email_address_or_password"));
          } else if (err?.code === 'auth/billing-not-enabled') {
            // TODO: hata mesajı geri açılacaktır.
            // toast.error(t("phone_not_found"));
            toast.error(t("wrong_email_address_or_password"));
          } else {
            toast.error(t("wrong_email_address_or_password"));
          }

          setShowVerification(false);
          setPhoneNumberDisable(false);
          setVerificationKey(prev => prev + 1);
        } else {
          toast.error(t("wrong_email_address_or_password"));
          setShowVerification(false);
          setPhoneNumberDisable(false);
          setVerificationKey(prev => prev + 1);
        }
      });
  };


  return (
    <PhoneNumberFormContainer>
      {showVerification ? (
        <Fade in timeout={600}>
          <VerificationOverlay>
            <PhoneVerification
              key={verificationKey}
              phoneNumber={phoneNumber}
              onBack={() => {
                setShowVerification(false);
                setPhoneNumberDisable(false);
                setVerificationKey(prev => prev + 1);
              }}
              onVerify={handleEnterCode}
              onResendCode={handleResendCode}
              loading={verificationLoading}
              title={t("verify_your_phone")}
              subtitle={t("verification_code_subtitle")}
              verifyButtonText={t("verify_button")}
            />
          </VerificationOverlay>
        </Fade>
      ) : (
        <>
          <Fade in timeout={600}>
            <PhoneNumberFormFieldContainer>
              <PhoneNumberInputContainer>
                <CountryCodeTextField
                  name="countryCode"
                  id="countryCode"
                  label={t("country_code")}
                  inputProps={{
                    maxLength: 4,
                  }}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={`${values.countryCode}`}
                  error={Boolean(errors.countryCode) && touched.countryCode}
                  helperText={touched.countryCode && errors.countryCode}
                  disabled={true}
                  variant="outlined"
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                    ),
                  }}
                />
                <InputMask
                  mask="999 999 99 99"
                  value={values.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  onBlur={handleBlur}
                  disabled={phoneNumberDisable}
                >
                  {(inputProps) => (
                    <StyledPhoneTextField
                      {...inputProps}
                      fullWidth
                      name="phoneNumber"
                      id="phoneNumber"
                      label={t("phone_number")}
                      error={Boolean(errors.phoneNumber) && touched.phoneNumber}
                      helperText={touched.phoneNumber && errors.phoneNumber}
                      variant="outlined"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSubmit();
                      }}
                      InputProps={{
                        startAdornment: (
                          <Phone sx={{ color: appColors.gray500, mr: 1, fontSize: 20 }} />
                        ),
                      }}
                    />
                  )}
                </InputMask>
              </PhoneNumberInputContainer>
            </PhoneNumberFormFieldContainer>
          </Fade>

          {showCaptcha && (
            <RecaptchaContainer>
              <RecaptchaV2Host
                siteKey={siteKey || process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                hl="tr"
                resetSignal={visibleRecaptchaKey}
                onChange={(token) => {
                  if (onCaptcha) {
                    onCaptcha(token ?? null);
                  } else {
                    setIsCaptchaVerifiedLocal(!!token);
                    setCaptchaToken(token ?? null);
                  }
                }}
                onExpired={() => setVisibleRecaptchaKey((k) => k + 1)}
                onError={() => setVisibleRecaptchaKey((k) => k + 1)}
              />
            </RecaptchaContainer>
          )}

          <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
                gap: 1.5,
                width: "100%",

              }}
            >
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={(() => {
                  const hasPhoneError = Boolean(errors.phoneNumber && touched.phoneNumber);
                  const hasCountryCodeError = Boolean(errors.countryCode && touched.countryCode);

                  const cleanPhoneNumber = values.phoneNumber?.replace(/\s/g, '') || '';
                  const isValidPhoneNumber = cleanPhoneNumber.length === 10 && /^[0-9]+$/.test(cleanPhoneNumber);
                  const isValidCountryCode = values.countryCode && /^\d+$/.test(values.countryCode);

                  const isFormInvalid = hasPhoneError || hasCountryCodeError || !isValidPhoneNumber || !isValidCountryCode;

                  const isCaptchaRequired = showCaptcha && !isCaptchaVerifiedFinal;

                  const isDisabled = phoneNumberDisable || isFormInvalid || isCaptchaRequired;
                  return isDisabled;
                })()}
                sx={{
                  width: "100%",
                  borderRadius: "12px",
                  padding: "0.875rem",
                  background: appColors.primary,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",

                  transition: "all 0.2s ease",
                  "&:hover": {
                    background: appColors.primaryDark,

                  },
                  "&:disabled": {
                    background: appColors.gray200,
                    transform: "none",

                  }
                }}
              >
                {t("login")}
              </Button>

            </Box>
        </>
      )}
    </PhoneNumberFormContainer>
  );
};

export default PhoneNumberForm;
